import { ReactNode, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NJButton, NJIconButton, NJText, NJTooltip } from '@engie-group/fluid-design-system-react';
import { usePrototypeNav } from './PrototypeNavContext';
import { CommentsPanel } from './comments/CommentsPanel';
import { CommentsOverlay } from './comments/CommentsOverlay';
import { fetchComments } from './bffComments';
import { S } from './studioTokens';

export interface PrototypeStudioLayoutProps {
  title: string;
  children: ReactNode;
  /** Sub-page identifier for prototypes with internal navigation (e.g. active plant, tab).
   *  When provided, comments are scoped to `<slug>--<commentScope>` instead of just `<slug>`. */
  commentScope?: string;
}

export default function PrototypeStudioLayout({ title, children, commentScope }: PrototypeStudioLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { prototypes, commentsVisible, setCommentsVisible } = usePrototypeNav();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [pinMode, setPinMode] = useState(false);
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);
  const [relocateId, setRelocateId] = useState<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);

  const { context } = usePrototypeNav();
  const protoBase = `/${context}/prototype`;
  const homeBase  = `/${context}`;

  const baseSlug = location.pathname.replace(new RegExp(`^/${context}/prototype/`), '');
  const slug = commentScope ? `${baseSlug}--${commentScope}` : baseSlug;
  const visibleProtos = prototypes.filter((p) => p.meta.status !== 'archived');
  const currentIndex = visibleProtos.findIndex((p) => p.slug === baseSlug);
  const prev = currentIndex > 0 ? visibleProtos[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < visibleProtos.length - 1 ? visibleProtos[currentIndex + 1] : null;

  const commentsEnabled = !!slug;

  const hideComments = () => {
    setCommentsVisible(false);
    setCommentsOpen(false);
    setPinMode(false);
    setHighlightCommentId(null);
    setRelocateId(null);
  };

  useEffect(() => {
    setHighlightCommentId(null);
    setRelocateId(null);
    setPinMode(false);
  }, [slug]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setPinMode(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', slug],
    queryFn: () => fetchComments(slug),
    enabled: commentsEnabled,
    staleTime: 30_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: 'var(--nj-core-font-family-sans)' }}>

      {/* ── Header ── */}
      <header style={{
        minHeight: 'var(--nj-semantic-size-spacing-48)',
        background: 'var(--nj-semantic-color-background-neutral-primary-default)',
        color: 'var(--nj-semantic-color-text-neutral-primary-default)',
        borderBottom: 'var(--nj-semantic-size-border-width) solid var(--nj-semantic-color-border-neutral-subtle-default)',
        display: 'flex', alignItems: 'center',
        padding: 'var(--nj-semantic-size-spacing-4) var(--nj-semantic-size-spacing-12)',
        flexShrink: 0,
        justifyContent: 'space-between',
        gap: 'var(--nj-semantic-size-spacing-8)',
        flexWrap: 'wrap',
      }}>

        {/* Left: breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-4)', minWidth: 0, overflow: 'hidden', flex: '1 1 0' }}>
          <NJButton label="Studio" icon="grid_view" href={homeBase} variant="secondary" emphasis="minimal" scale="sm"
            onClick={(event) => {
              if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
                event.preventDefault();
                navigate(homeBase);
              }
            }} />
          <NJText as="span" scale="sm" aria-hidden="true">›</NJText>
          <NJText as="span" scale="sm" style={{
            marginLeft: 'var(--nj-semantic-size-spacing-12)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {title}
          </NJText>
        </div>

        {/* Right: comments + navigation */}
        <div role="group" aria-label="Outils du prototype" style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-4)', flexShrink: 0, flexWrap: 'wrap' }}>
          {commentsEnabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-semantic-size-spacing-4)', marginRight: 'var(--nj-semantic-size-spacing-4)' }}>

              {/* Mode annotation */}
              <NJTooltip text={pinMode ? 'Quitter le mode annotation (Échap)' : 'Créer un commentaire sur la page'} placement="bottom" hasArrow>
              <div>
              {!pinMode ? (
                <NJIconButton
                  label="Annoter un élément sur la page"
                  icon="edit_note" variant="tertiary" scale="sm"
                  ariaPressed={false}
                  onClick={() => { setCommentsVisible(true); setPinMode(true); setCommentsOpen(false); }}
                />
              ) : (
                <NJIconButton
                  label="Quitter le mode annotation (Échap)"
                  icon="edit_note" variant="primary" scale="sm" ariaPressed
                  onClick={() => setPinMode(false)}
                />
              )}
              </div>
              </NJTooltip>
              <NJTooltip text="Voir les commentaires" placement="bottom" hasArrow>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <NJIconButton
                    label={`Voir les commentaires (${comments.length})`}
                    icon="forum" variant={commentsOpen ? 'primary' : 'tertiary'} scale="sm"
                    ariaPressed={commentsOpen} onClick={() => setCommentsOpen(true)} />
                  {comments.length > 0 && (
                    <NJText as="span" scale="xs" aria-hidden="true">
                      {comments.length > 99 ? '99+' : comments.length}
                    </NJText>
                  )}
                </div>
              </NJTooltip>
              <NJTooltip text={commentsVisible ? 'Masquer les commentaires sur la page' : 'Afficher les commentaires sur la page'} placement="bottom" hasArrow>
              <div>
              <NJIconButton
                label={commentsVisible ? 'Masquer les commentaires sur la page' : 'Afficher les commentaires sur la page'}
                icon={commentsVisible ? 'visibility' : 'visibility_off'}
                variant="tertiary" scale="sm"
                ariaPressed={!commentsVisible}
                onClick={() => commentsVisible ? hideComments() : setCommentsVisible(true)}
              />
              </div>
              </NJTooltip>
            </div>
          )}

          {currentIndex >= 0 && (
            <NJText as="span" scale="xs">
              {currentIndex + 1} / {visibleProtos.length}
            </NJText>
          )}

          <NavArrow direction="prev" label={prev?.meta.title ?? null} disabled={!prev}
            onClick={() => prev && navigate(`${protoBase}/${prev.slug}`)} />
          <NavArrow direction="next" label={next?.meta.title ?? null} disabled={!next}
            onClick={() => next && navigate(`${protoBase}/${next.slug}`)} />
        </div>
      </header>

      {/* ── Content ── */}
      <main ref={contentRef} style={{
        flex: 1, overflow: 'auto', position: 'relative',
        backgroundColor: 'var(--nj-core-color-grey-50)',
        backgroundImage: 'radial-gradient(circle, var(--nj-core-color-grey-300) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}>
        {children}
        {commentsEnabled && commentsVisible && (
          <CommentsOverlay
            key={slug}
            slug={slug}
            contentRef={contentRef}
            relocateId={relocateId}
            onRelocated={() => setRelocateId(null)}
            comments={comments}
            active={pinMode}
            highlightCommentId={highlightCommentId}
            onPinOpened={(id) => setHighlightCommentId(id)}
          />
        )}
      </main>

      {/* ── Comments panel ── */}
      {commentsEnabled && commentsOpen && (
        <CommentsPanel
          key={slug}
          slug={slug}
          highlightCommentId={highlightCommentId}
          onHighlightPin={(id) => { setCommentsVisible(true); setHighlightCommentId(id); setCommentsOpen(false); }}
          onMovePin={(id) => { setCommentsVisible(true); setRelocateId(id); setCommentsOpen(false); }}
          onClose={() => setCommentsOpen(false)}
        />
      )}

      {/* ── Footer ── */}
      <footer style={{
        height: 28,
        background: S.bg,
        borderTop: `1px solid ${S.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, color: S.textDim, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Prototype</span>
        {baseSlug && (
          <span style={{ fontFamily: 'monospace', fontSize: 10, color: S.textDim, opacity: 0.7 }}>
            {baseSlug}{commentScope ? ` › ${commentScope}` : ''}
          </span>
        )}
      </footer>
    </div>
  );
}

function NavArrow({ direction, label, disabled, onClick }: {
  direction: 'prev' | 'next';
  label: string | null;
  disabled: boolean;
  onClick: () => void;
}) {
  const text = `${direction === 'prev' ? 'Prototype précédent' : 'Prototype suivant'}${label ? ` : ${label}` : ''}`;
  return (
    <NJTooltip text={text} placement="bottom" hasArrow>
    <div>
    <NJIconButton
      label={text}
      icon={direction === 'prev' ? 'chevron_left' : 'chevron_right'}
      variant="tertiary" scale="sm" isDisabled={disabled} onClick={onClick}
    />
    </div>
    </NJTooltip>
  );
}
